import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import IconButton from '@mui/joy/IconButton';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import DashboardPageHeader from '../../components/DashboardPageHeader';
import DashboardTable from '../../components/DashboardTable';
import PageStack from '../../components/PageStack';
import { LAYOUT } from '../../theme/layoutTokens';
import { attributeSetsApi } from '../../api/attribute-sets.api';
import { AttributeSet } from '../../types/attribute-set';

const AttributeSetList: React.FC = () => {
  const navigate = useNavigate();
  const [sets, setSets] = useState<AttributeSet[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSets();
  }, []);

  const loadSets = async () => {
    try {
      setLoading(true);
      const data = await attributeSetsApi.getAll();
      setSets(data);
    } catch (error) {
      console.error('Error loading attribute sets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (reference: string) => {
    if (window.confirm('¿Eliminar este Attribute Set?')) {
      try {
        await attributeSetsApi.delete(reference);
        setSets((prev) => prev.filter((s) => s.reference !== reference));
      } catch (error) {
        console.error('Error deleting set:', error);
      }
    }
  };

  return (
    <PageStack spacing={LAYOUT.stackSurfaceGap}>
      <DashboardPageHeader
        title="Attribute Sets"
        action={
          <Button color="primary" onClick={() => navigate('/attribute-sets/new')}>
            Nuevo Set
          </Button>
        }
      />

      <Sheet variant="outlined" sx={{ overflow: 'auto', borderRadius: 'md', boxShadow: 'sm', p: 0 }}>
        {sets.length === 0 ? (
          <Typography level="body-sm" sx={{ py: 4, textAlign: 'center', color: 'neutral.500' }}>
            No hay Attribute Sets creados aún.
          </Typography>
        ) : (
          <DashboardTable stickyHeader>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Atributos</th>
                <th style={{ textAlign: 'right' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {sets.map((set) => (
                <tr key={set.reference}>
                  <td>{set.name}</td>
                  <td>{set.items?.length || 0}</td>
                  <td>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="primary"
                        onClick={() => navigate(`/attribute-sets/${set.reference}/edit`)}
                      >
                        <EditRoundedIcon />
                      </IconButton>
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="danger"
                        onClick={() => handleDelete(set.reference)}
                      >
                        <DeleteRoundedIcon />
                      </IconButton>
                    </Box>
                  </td>
                </tr>
              ))}
            </tbody>
          </DashboardTable>
        )}
      </Sheet>
    </PageStack>
  );
};

export default AttributeSetList;
